import { ArrowLeft, Check, Cloud, Eye, FileText, Globe, Loader2, Save } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useParams } from 'react-router-dom';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Separator } from '@/components/ui/separator';
// Tabs removed
import { Textarea } from '@/components/ui/textarea';
import { apiUrl } from '@/config/api';

const API_BASE = apiUrl('/api/catalogs');

interface Catalog {
  id: string;
  tenantId: string;
  title: string;
  academicYear: number;
  version: number;
  status: 'draft' | 'preview' | 'published' | 'archived';
  publishedAt: string | null;
  updatedAt: string;
  config: Record<string, unknown>;
  description?: string;
}

export default function CatalogDetail() {
  const { t } = useTranslation();
  const { id } = useParams();
  const isNew = id === 'new';

  const [loading, setLoading] = useState(false);
  const [catalog, setCatalog] = useState<Catalog>({
    id: id || '',
    tenantId: '',
    title: '2026年度 学生募集要項',
    academicYear: 2026,
    version: 1,
    status: 'draft',
    publishedAt: null,
    updatedAt: new Date().toISOString(),
    config: {},
    description: '',
  });

  const handleSave = async () => {
    setLoading(true);
    // Simulate save
    setTimeout(() => setLoading(false), 1000);
  };

  const handleReleaseAction = async (action: 'submit_for_preview' | 'publish' | 'archive') => {
    if (!confirm('Are you sure you want to proceed with this release action?')) return;
    setLoading(true);
    try {
      const res = await fetch(`${API_BASE}/${id}/release`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action }),
      });
      if (res.ok) {
        // Update state based on action for demo
        if (action === 'submit_for_preview') setCatalog({ ...catalog, status: 'preview' });
        if (action === 'publish')
          setCatalog({ ...catalog, status: 'published', publishedAt: new Date().toISOString() });
        if (action === 'archive') setCatalog({ ...catalog, status: 'archived' });
      }
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-5xl mx-auto space-y-6 p-8">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Link
            to="/admin/master/catalogs"
            className="p-2 hover:bg-white rounded-full text-gray-500 transition-colors border border-transparent hover:border-gray-200 hover:shadow-sm"
          >
            <ArrowLeft className="w-5 h-5" />
          </Link>
          <div>
            <div className="flex items-center gap-3">
              <h1 className="text-2xl font-bold text-gray-900">{isNew ? 'New Catalog' : catalog.title}</h1>
              {!isNew && (
                <Badge
                  className={
                    catalog.status === 'published'
                      ? 'bg-green-100 text-green-700'
                      : catalog.status === 'preview'
                        ? 'bg-blue-100 text-blue-700'
                        : 'bg-gray-100 text-gray-700'
                  }
                >
                  {catalog.status.toUpperCase()}
                </Badge>
              )}
            </div>
            <p className="text-gray-500 text-sm mt-1">
              ID: {catalog.id} • Ver.{catalog.version}
            </p>
          </div>
        </div>
        <div className="flex gap-2">
          <Button onClick={handleSave} disabled={loading}>
            {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            <Save className="mr-2 h-4 w-4" />
            {t('common.save')}
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-3 gap-8">
        {/* Main Content */}
        <div className="col-span-2 space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>基本情報</CardTitle>
              <CardDescription>カタログの基本設定</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <label htmlFor="academicYear" className="text-sm font-medium">
                    対象年度 (Academic Year)
                  </label>
                  <Input
                    id="academicYear"
                    type="number"
                    value={catalog.academicYear}
                    onChange={(e) => setCatalog({ ...catalog, academicYear: parseInt(e.target.value, 10) })}
                    disabled={!isNew && catalog.status !== 'draft'}
                  />
                </div>
                <div className="space-y-2">
                  <label htmlFor="version" className="text-sm font-medium">
                    バージョン
                  </label>
                  <Input id="version" value={catalog.version} disabled className="bg-gray-50" />
                </div>
              </div>
              <div className="space-y-2">
                <label htmlFor="title" className="text-sm font-medium">
                  タイトル
                </label>
                <Input
                  id="title"
                  value={catalog.title}
                  onChange={(e) => setCatalog({ ...catalog, title: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <label htmlFor="description" className="text-sm font-medium">
                  説明 (管理用)
                </label>
                <Textarea
                  id="description"
                  value={catalog.description}
                  onChange={(e) => setCatalog({ ...catalog, description: e.target.value })}
                  rows={3}
                />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Catalog Configuration</CardTitle>
              <CardDescription>JSON Configuration for this catalog version</CardDescription>
            </CardHeader>
            <CardContent>
              <Textarea
                className="font-mono text-xs h-64 bg-slate-900 text-green-400"
                value={JSON.stringify(catalog.config, null, 2)}
                onChange={(e) => {
                  try {
                    const parsed = JSON.parse(e.target.value);
                    setCatalog({ ...catalog, config: parsed });
                  } catch {
                    // ignore parse error for controlled input, usually handle with local state
                  }
                }}
              />
            </CardContent>
          </Card>
        </div>

        {/* Sidebar: Release Management (US-Dxx) */}
        <div className="col-span-1 space-y-6">
          <Card className="border-l-4 border-l-blue-500 shadow-sm">
            <CardHeader>
              <CardTitle className="text-lg">Release Management</CardTitle>
              <CardDescription>ステータス変更と公開管理</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex flex-col gap-2">
                <div className="flex justify-between items-center text-sm">
                  <span className="text-gray-500">Current Status</span>
                  <span className="font-bold">{catalog.status.toUpperCase()}</span>
                </div>
                <Separator />

                {/* Action Buttons based on State Machine */}
                {catalog.status === 'draft' && (
                  <Button
                    className="w-full bg-blue-600 hover:bg-blue-700"
                    onClick={() => handleReleaseAction('submit_for_preview')}
                  >
                    <Eye className="mr-2 h-4 w-4" />
                    Submit for Preview
                  </Button>
                )}

                {catalog.status === 'preview' && (
                  <div className="space-y-2">
                    <Button
                      className="w-full bg-green-600 hover:bg-green-700"
                      onClick={() => handleReleaseAction('publish')}
                    >
                      <Cloud className="mr-2 h-4 w-4" />
                      Publish Catalog
                    </Button>
                    <Button
                      variant="outline"
                      className="w-full"
                      onClick={() => setCatalog({ ...catalog, status: 'draft' })} // Withdraw
                    >
                      Withdraw to Draft
                    </Button>
                  </div>
                )}

                {catalog.status === 'published' && (
                  <div className="space-y-4">
                    <div className="bg-green-50 p-4 rounded text-sm text-green-800 border border-green-200">
                      <Check className="inline-block w-4 h-4 mr-1" />
                      This catalog is live.
                    </div>
                    <Button variant="destructive" className="w-full" onClick={() => handleReleaseAction('archive')}>
                      Archive
                    </Button>
                  </div>
                )}

                {catalog.status === 'archived' && (
                  <div className="bg-gray-100 p-4 rounded text-center text-gray-500 italic">
                    Archived on {catalog.updatedAt.split('T')[0]}
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="text-sm">Quick Links</CardTitle>
            </CardHeader>
            <CardContent className="flex flex-col gap-2">
              <Button variant="ghost" className="justify-start gap-2">
                <FileText className="w-4 h-4" /> Billing Items
              </Button>
              <Button variant="ghost" className="justify-start gap-2">
                <Globe className="w-4 h-4" /> Public Page
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
